import { ethers } from 'ethers';
import { LEVER_SWAP_CONFIG } from './swapConfig';
import { fetchSushiSwapData } from './sushiApi';
import { getAllowanceStatus } from './allowance';
import { applyBuyFactor, applySwapMultiplier, ERC20_ABI, formatTokenFloor, normalizeSwapMultiplier, safeNormalize, STARTAX_SWAP_ABI } from './swapUtils';
import { mergeValidationResults, validateApprovalChecks, validatePreparedSwap, validateSellQuoteResult } from './validateSwap';
import { verifySellQuoteAgainstPool } from './quoteVerification';
import { buildEthBalanceCheck, throwInsufficientBalanceError } from './balanceChecks';

export async function buildSellSwapDryRun({ signer, walletAddress, swapMultiplier = LEVER_SWAP_CONFIG.DEFAULT_SWAP_MULTIPLIER }) {
  if (!signer) throw new Error('Missing wallet signer.');
  const provider = signer.provider;
  const accountAddress = safeNormalize(walletAddress || (await signer.getAddress()));

  const starTax = new ethers.Contract(safeNormalize(LEVER_SWAP_CONFIG.PR_ADDRESS), STARTAX_SWAP_ABI, provider);
  const selectedSwapMultiplier = normalizeSwapMultiplier(swapMultiplier);
  const basePR = await starTax.transactionMinimum();
  const standardAmountPR = applyBuyFactor(basePR);
  const amountPR = applySwapMultiplier(standardAmountPR, selectedSwapMultiplier);

  const prToken = new ethers.Contract(safeNormalize(LEVER_SWAP_CONFIG.PR_ADDRESS), ERC20_ABI, provider);
  const prBalance = await prToken.balanceOf(accountAddress);
  if (prBalance < BigInt(amountPR)) {
    throw throwInsufficientBalanceError({
      code: 'INSUFFICIENT_TAX_BALANCE',
      message: 'Insufficient ★TAX balance for SELL swap.',
      requiredRaw: amountPR.toString(),
      balanceRaw: prBalance.toString()
    });
  }

  const tokenIn = safeNormalize(LEVER_SWAP_CONFIG.PR_ADDRESS);
  const tokenOut = LEVER_SWAP_CONFIG.NATIVE_ADDRESS;

  const { url, data: swapData } = await fetchSushiSwapData({
    tokenIn,
    tokenOut,
    amount: amountPR.toString(),
    sender: accountAddress
  });

  const txData = swapData.tx;
  const baseValidation = validatePreparedSwap({ mode: 'SELL', walletAddress: accountAddress, txData, tokenIn, tokenOut, amount: amountPR.toString() });

  const allowanceChecks = [
  await getAllowanceStatus({
    provider,
    tokenAddress: LEVER_SWAP_CONFIG.PR_ADDRESS,
    ownerAddress: accountAddress,
    spender: LEVER_SWAP_CONFIG.PERMIT2_ADDRESS,
    requiredAmount: amountPR
  })];

  if (txData?.to) {
    allowanceChecks.push(await getAllowanceStatus({
      provider,
      tokenAddress: LEVER_SWAP_CONFIG.PR_ADDRESS,
      ownerAddress: accountAddress,
      spender: txData.to,
      requiredAmount: amountPR
    }));
  }

  const balanceCheck = await buildEthBalanceCheck({
    provider,
    accountAddress,
    mode: 'SELL',
    txData,
    valueRaw: '0',
    approvalCount: allowanceChecks.filter((item) => !item.isEnough).length
  });

  if (!balanceCheck.ok) {
    throw throwInsufficientBalanceError({
      code: 'INSUFFICIENT_OPTIMISM_ETH_GAS_BALANCE',
      message: 'Insufficient ETH balance on Optimism to pay SELL gas.',
      balanceCheck
    });
  }

  const quoteVerification = await verifySellQuoteAgainstPool({
    provider,
    amountTaxIn: amountPR,
    sushiAssumedAmountOut: swapData.assumedAmountOut || swapData.amountOut
  });

  const approvalValidation = validateApprovalChecks({ allowanceChecks });
  const quoteValidation = validateSellQuoteResult(quoteVerification);
  const validation = mergeValidationResults(baseValidation, approvalValidation, quoteValidation);

  return {
    dryRun: true,
    mode: 'SELL',
    walletAddress: accountAddress,
    sushiUrl: url,
    txData,
    tokenIn,
    tokenOut,
    baseAmountRaw: basePR.toString(),
    standardAmountRaw: standardAmountPR.toString(),
    swapMultiplier: selectedSwapMultiplier,
    amountInRaw: amountPR.toString(),
    amountInDisplay: formatTokenFloor(amountPR, 18),
    lastSwapAmountDisplay: formatTokenFloor(amountPR, 18),
    allowanceChecks,
    balanceCheck,
    validation,
    swapData,
    quoteVerification
  };
}
